import { noteEvent } from './diagnostics';

/**
 * Getting a video off a web page and into someone's phone.
 *
 * This is fiddlier than it looks, and the rules were learned on the MHA share
 * page rather than derived:
 *
 * - **Web Share with files is the only route into the iOS camera roll.** A plain
 *   `<a download>` saves to Files, where most people never look again. The share
 *   sheet exposes "Save Video", which writes to Photos.
 * - **`navigator.canShare({ files })` is the only trustworthy check.** Android
 *   Chrome will claim `navigator.share` exists and then reject a file payload.
 * - **`<a download>` is ignored cross-origin.** A signed storage URL is on
 *   Supabase's domain, so the filename hint does nothing and the browser may
 *   navigate instead of saving. The URL has to carry
 *   `Content-Disposition: attachment` itself — which is what `get-clip`'s
 *   downloadUrl is for.
 * - **AbortError is not a failure.** It means they dismissed the sheet.
 */

export type SaveOutcome = 'shared' | 'downloaded' | 'cancelled';

/** True when this browser can share actual files, not just links. */
export function canShareFile(file: File): boolean {
  return typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
}

/**
 * Save a blob we already hold — the "Save" button, as distinct from "Share".
 *
 * On iOS these end up in the same place on purpose, because there is only one
 * place that works: `<a download>` saves into Files, and nothing in Files is in
 * the camera roll. The share sheet's "Save Video" is the only route into Photos,
 * so a Save button that refuses to use it is a Save button that does not save.
 * Everywhere else the download is the better answer — no sheet to dismiss.
 *
 * Do not make this async ahead of `navigator.share`. `File` construction and
 * `canShareFile` are both synchronous so the call still sits inside the click's
 * transient activation; an `await` before it loses that and iOS rejects the
 * share with NotAllowedError. The failure only shows on a real device.
 */
export async function saveBlob(
  blob: Blob,
  filename: string,
  mimeType: string,
): Promise<SaveOutcome> {
  const file = new File([blob], filename, { type: mimeType });

  if (canShareFile(file)) {
    try {
      await navigator.share({ files: [file], title: 'The Incomer' });
      noteEvent('saved via share sheet');
      return 'shared';
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
      noteEvent('share sheet failed, falling back to download');
    }
  }

  const url = URL.createObjectURL(blob);
  try {
    triggerDownload(url, filename);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
  noteEvent('saved via download');
  return 'downloaded';
}

/**
 * Share a clip that lives behind a URL. Used by the retrieval page, where the
 * file is in storage rather than in memory.
 *
 * `playUrl` is fetched into a blob so the share sheet gets a real file; if that
 * is not possible we fall back to `downloadUrl`, which carries the attachment
 * header and so saves rather than navigates.
 */
export async function shareFromUrl(
  playUrl: string,
  downloadUrl: string,
  filename: string,
): Promise<SaveOutcome> {
  try {
    const res = await fetch(playUrl);
    if (res.ok) {
      const blob = await res.blob();
      const file = new File([blob], filename, { type: blob.type || 'video/mp4' });
      if (canShareFile(file)) {
        try {
          await navigator.share({ files: [file], title: 'The Incomer' });
          return 'shared';
        } catch (err) {
          if (err instanceof DOMException && err.name === 'AbortError') return 'cancelled';
        }
      }
    }
  } catch {
    // Offline, or the signed URL expired while the page sat open. The download
    // link is still worth trying — it may fail visibly, which is better than
    // this silently doing nothing.
  }

  triggerDownload(downloadUrl, filename);
  return 'downloaded';
}

/**
 * The anchor has to be in the document before it is clicked. Safari ignores
 * `click()` on a detached element entirely — no download, no error, nothing —
 * which is exactly how the result screen's Save button came to do nothing on
 * iOS. Everything goes through here rather than hand-rolling an anchor at the
 * call site, because that is the mistake that was made.
 */
function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
